import { useEffect } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Download, ArrowLeft, AlertCircle, Mail, Clock, Scale, Building2 } from "lucide-react";
import { printPolicyPage } from "@/lib/printPolicy";

const handleDownload = (title) => printPolicyPage({
  title,
  contentElementId: "reclami-content",
  headerHtml: `<div class="header"><strong>COLUCCI ASSICURATORI</strong><br>CHRISTIAN E CIRO COLUCCI S.A.S. &mdash; P.IVA 07618011212 &mdash; RUI A000464752</div>`,
});

export default function ReclamiPage() {
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("download") === "1") {
      const t = setTimeout(() => handleDownload("Gestione Reclami - Colucci Assicuratori"), 600);
      return () => clearTimeout(t);
    }
  }, [params]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="reclami-page">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-12">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="text-sm text-[#4A5E51] hover:text-[#163321] flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> Torna alla Home
            </Link>
            <Button onClick={() => handleDownload("Gestione Reclami - Colucci Assicuratori")} variant="outline" className="rounded-xl border-[#E5E2DB] text-sm gap-2" data-testid="download-reclami-btn">
              <Download className="w-4 h-4" /> Scarica PDF
            </Button>
          </div>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-xl bg-[#D97B58]/10 flex items-center justify-center">
              <Scale className="w-5 h-5 text-[#D97B58]" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-medium text-[#163321]">Gestione Reclami</h1>
              <p className="text-xs text-[#4A5E51] mt-0.5">Procedura conforme al <strong>D.Lgs. 209/2005</strong> (Codice delle Assicurazioni Private), al <strong>Regolamento IVASS 24/2008</strong> (gestione dei reclami da parte degli intermediari) e al <strong>Regolamento IVASS 41/2018</strong> (informativa precontrattuale e set informativo).</p>
            </div>
          </div>

          <article id="reclami-content" className="prose prose-sm max-w-none text-[#4A5E51] text-sm leading-relaxed space-y-6">
            <section className="bg-[#F5F4F0] rounded-xl p-5 border-l-4 border-[#D97B58]">
              <p className="text-[#163321] font-medium mb-1.5 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> Il tuo diritto di reclamare
              </p>
              <p className="text-xs">
                Se ritieni di aver subito un pregiudizio dall'operato di Colucci Assicuratori o di uno dei suoi collaboratori nell'attività di distribuzione assicurativa, hai diritto a presentare reclamo secondo la procedura qui descritta. Riceverai risposta motivata entro i termini di legge.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> 1. Reclamo all'intermediario (primo livello)
              </h2>
              <p>
                Il reclamo può essere presentato in forma scritta a Colucci Assicuratori utilizzando uno dei seguenti canali:
              </p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong>Posta raccomandata A/R</strong>: CHRISTIAN E CIRO COLUCCI S.A.S. — Ufficio Reclami — Via Armando Diaz 182/L, 80055 Portici (NA)</li>
                <li><strong>Email</strong>: <a href="mailto:reclami@colucciassicuratori.com" className="text-[#163321] underline">reclami@colucciassicuratori.com</a></li>
                <li><strong>PEC</strong>: <a href="mailto:coluccisas@pec.it" className="text-[#163321] underline">coluccisas@pec.it</a></li>
                <li><strong>Fax</strong>: presso la sede legale, su richiesta</li>
              </ul>
              <p>
                <strong>Contenuto minimo del reclamo:</strong> dati identificativi del reclamante (nome, cognome, codice fiscale, indirizzo), riferimenti della polizza o della richiesta (se applicabili), descrizione chiara del fatto oggetto del reclamo, eventuali documenti a supporto.
              </p>
              <p className="flex items-start gap-2 bg-[#163321]/5 rounded-lg px-3 py-2 text-xs">
                <Clock className="w-3.5 h-3.5 text-[#163321] mt-0.5 flex-shrink-0" />
                <span><strong>Tempi di risposta</strong>: ai sensi dell'<strong>art. 8 Reg. IVASS 24/2008</strong>, l'intermediario fornisce riscontro scritto entro <strong>45 giorni</strong> dalla ricezione del reclamo. Qualora il reclamo abbia ad oggetto il comportamento dell'impresa, esso viene tempestivamente trasmesso alla compagnia competente con relativa comunicazione al reclamante. Eventuali proroghe eccezionali saranno comunicate per iscritto e motivatamente.</span>
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2 flex items-center gap-2">
                <Scale className="w-4 h-4" /> 2. Reclamo all'Impresa di Assicurazione
              </h2>
              <p>
                Qualora il reclamo riguardi direttamente il comportamento dell'impresa di assicurazione (es. gestione del sinistro, liquidazione, rimborsi), esso va indirizzato all'Ufficio Reclami della <strong>compagnia assicurativa emittente della polizza</strong>, i cui recapiti sono indicati nel <strong>DIP Aggiuntivo</strong>, nel <strong>Set Informativo (Allegato 4-bis e Allegato 4-ter Reg. IVASS 41/2018)</strong> e nelle Condizioni di Assicurazione della polizza.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">3. Reclamo all'IVASS (secondo livello)</h2>
              <p>
                Se la risposta dell'intermediario o dell'impresa non è soddisfacente — o se non è pervenuta entro 45 giorni — il reclamante può rivolgersi a <strong>IVASS (Istituto per la Vigilanza sulle Assicurazioni)</strong>:
              </p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong>Indirizzo</strong>: IVASS — Servizio Tutela del Consumatore, Via del Quirinale 21, 00187 Roma</li>
                <li><strong>Modulo e canali ufficiali</strong>: scaricabili da <a href="https://www.ivass.it/consumatori/reclami/" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">www.ivass.it/consumatori/reclami</a> (recapiti PEC, fax e indirizzi sempre aggiornati sul sito istituzionale IVASS)</li>
              </ul>
              <p>
                Al reclamo IVASS vanno allegati: copia del reclamo già presentato all'intermediario/impresa, copia dell'eventuale risposta ricevuta, ogni documento utile.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">4. Sistemi di risoluzione alternativa delle controversie (ADR)</h2>
              <p>
                Prima di adire l'Autorità Giudiziaria, il reclamante ha la facoltà di avvalersi dei seguenti strumenti:
              </p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>
                  <strong>Mediazione obbligatoria</strong> ex art. 5 D.Lgs. 28/2010 presso un Organismo di Mediazione iscritto al Ministero della Giustizia (condizione di procedibilità per le controversie in materia di contratti assicurativi).
                </li>
                <li>
                  <strong>Negoziazione assistita</strong> ex D.L. 132/2014 conv. L. 162/2014.
                </li>
                <li>
                  <strong>Arbitro per le Controversie Finanziarie (ACF)</strong> per controversie relative a prodotti di investimento assicurativo IBIP (max €500.000): <a href="https://www.acf.consob.it" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">www.acf.consob.it</a>.
                </li>
                <li>
                  <strong>Piattaforma ODR europea</strong> (Online Dispute Resolution): <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" className="text-[#163321] underline">ec.europa.eu/consumers/odr/</a>.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-heading text-[#163321] mb-2">5. Autorità Giudiziaria</h2>
              <p>
                Resta in ogni caso impregiudicato il diritto del reclamante di rivolgersi all'<strong>Autorità Giudiziaria</strong>. Per le controversie con un consumatore è competente in via esclusiva il Foro del luogo di residenza o domicilio del consumatore (art. 66-bis D.Lgs. 206/2005).
              </p>
            </section>

            <section className="bg-[#F5F4F0] rounded-xl p-5">
              <h2 className="text-lg font-heading text-[#163321] mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Contatti Ufficio Reclami
              </h2>
              <div className="space-y-1">
                <p><strong>Responsabile Reclami</strong>: Christian Colucci</p>
                <p><strong>Email</strong>: <a href="mailto:reclami@colucciassicuratori.com" className="text-[#163321] underline">reclami@colucciassicuratori.com</a></p>
                <p><strong>PEC</strong>: <a href="mailto:coluccisas@pec.it" className="text-[#163321] underline">coluccisas@pec.it</a></p>
                <p><strong>Telefono</strong>: <a href="tel:0817611000" className="text-[#163321] underline">081 761 1000</a></p>
                <p><strong>Orari</strong>: Lun-Ven 9:00-13:00 / 15:00-18:00</p>
              </div>
            </section>

            <section className="bg-white border border-[#E5E2DB] rounded-xl p-5">
              <h2 className="text-lg font-heading text-[#163321] mb-2">Registro Reclami</h2>
              <p className="text-xs">
                Colucci Assicuratori tiene un <strong>registro informatico dei reclami</strong> ricevuti, conforme all'art. 9 Reg. IVASS 24/2008, contenente data di ricezione, identificazione del reclamante, oggetto, esito e tempo di risposta. Il registro è conservato per <strong>almeno 5 anni</strong> dall'ultima registrazione, ai fini dei controlli IVASS e a tutela del reclamante.
              </p>
              <p className="text-xs mt-2">
                Eventuali dati personali presenti nei reclami sono trattati ai sensi del Reg. UE 2016/679 (GDPR) — vedi <Link to="/privacy" className="text-[#163321] underline">Privacy Policy</Link> e <Link to="/privacy/dpo" className="text-[#163321] underline">contatti DPO</Link>.
              </p>
            </section>
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}
